import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CareparvaReport.css';
import careparvaReportService from '../../services/careparvaReportService';
import { showToast } from '../../components/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileExcel,
  faUpload,
  faSyncAlt,
  faHistory,
  faSearch,
  faTimes,
  faSpinner,
  faChartLine,
  faClock,
  faLayerGroup,
  faCheckCircle,
  faExclamationTriangle,
  faList,
  faEye,
  faFileCsv,
} from '@fortawesome/free-solid-svg-icons';

const TABS = [
  { id: 0, label: 'All Entries', ptype: null, icon: faList },
  { id: 1, label: 'Intraday Details', ptype: 1, icon: faClock },
  { id: 2, label: 'Single Stock Details', ptype: 2, icon: faChartLine },
  { id: 3, label: 'Derivative Details', ptype: 3, icon: faLayerGroup },
];

const STATUS_FILTERS = [
  { label: 'All Statuses', value: '' },
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'CLOSED', value: 'CLOSED' },
  { label: 'INVALID', value: 'INVALID' },
  { label: 'REJECTED', value: 'REJECTED' },
];

const CareparvaReport = () => {
  const navigate = useNavigate();
  // Active Tab & Filters
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(1000);

  // In-Table Column Filters State (Subscriber Module Style)
  const [colFilters, setColFilters] = useState({
    service_name: '',
    symbol_name: '',
    action: '',
    p_action: '',
    status: '',
    p_status: '',
    p_reason: '',
  });

  const [createdDateRange, setCreatedDateRange] = useState([null, null]);
  const [startDate, endDate] = createdDateRange;

  const handleColFilterChange = (field, value) => {
    setColFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setColFilters({
      service_name: '',
      symbol_name: '',
      action: '',
      p_action: '',
      status: '',
      p_status: '',
      p_reason: '',
    });
    setCreatedDateRange([null, null]);
  };

  // Data States
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Client-side instant filter over loaded records
  const filteredEntries = useMemo(() => {
    return entries.filter((item) => {
      if (
        colFilters.service_name &&
        !String(item.service_name || '')
          .toLowerCase()
          .includes(colFilters.service_name.trim().toLowerCase())
      ) {
        return false;
      }
      if (
        colFilters.symbol_name &&
        !String(item.symbol_name || '')
          .toLowerCase()
          .includes(colFilters.symbol_name.trim().toLowerCase())
      ) {
        return false;
      }
      if (colFilters.action !== '') {
        if (String(item.action) !== String(colFilters.action)) return false;
      }
      if (colFilters.p_action !== '') {
        if (
          !String(item.p_action || '')
            .toUpperCase()
            .includes(colFilters.p_action.toUpperCase())
        ) {
          return false;
        }
      }
      if (colFilters.status !== '') {
        if (String(item.status) !== String(colFilters.status)) return false;
      }
      if (colFilters.p_status !== '') {
        if (
          String(item.p_status || '').toUpperCase() !==
          colFilters.p_status.toUpperCase()
        ) {
          return false;
        }
      }
      if (
        colFilters.p_reason &&
        !String(item.p_reason || '')
          .toLowerCase()
          .includes(colFilters.p_reason.trim().toLowerCase())
      ) {
        return false;
      }
      if (startDate) {
        const rawDateStr = item.p_created_on || item.n_created_on;
        if (!rawDateStr) return false;
        const itemDate = new Date(rawDateStr);
        if (isNaN(itemDate.getTime())) return false;

        const startOfDay = new Date(startDate);
        startOfDay.setHours(0, 0, 0, 0);

        if (endDate) {
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (itemDate < startOfDay || itemDate > endOfDay) return false;
        } else {
          const endOfDay = new Date(startDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (itemDate < startOfDay || itemDate > endOfDay) return false;
        }
      }
      return true;
    });
  }, [entries, colFilters, createdDateRange]);

  // Summary KPI State
  const [summary, setSummary] = useState({
    total: 0,
    intradayCount: 0,
    singleStockCount: 0,
    derivativeCount: 0,
    latestLog: null,
  });

  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Status History Modal State
  const [statusModalAdvice, setStatusModalAdvice] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Logs Modal State
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsList, setLogsList] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Fetch Summary KPIs
  const fetchSummary = useCallback(async () => {
    try {
      const data = await careparvaReportService.getSummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to load summary stats:', err);
    }
  }, []);

  // Fetch Entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const currentPtype = TABS[activeTab].ptype;
      const res = await careparvaReportService.getEntries({
        page,
        limit,
        ptype: currentPtype || undefined,
        search: searchTerm || undefined,
        status: selectedStatus || undefined,
      });

      setEntries(res.items || []);
      setTotalEntries(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Failed to load entries:', err);
      showToast.error('Failed to fetch entries list.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, limit, searchTerm, selectedStatus]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  // Search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  // Status Filter
  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage(1);
  };

  // Refresh
  const handleRefresh = () => {
    fetchSummary();
    fetchEntries();
    showToast.info('Refreshing data...');
  };

  // Drag & drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      setUploadError('Invalid file format. Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.');
      setSelectedFile(null);
      return;
    }
    setUploadError(null);
    setUploadResult(null);
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  // Submit Upload
  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload.');
      return;
    }

    const fileToUpload = selectedFile;
    // Close modal immediately on click as requested
    closeUploadModal();
    setUploading(true);
    showToast.info('Uploading and processing file...');

    try {
      const res = await careparvaReportService.uploadFile(fileToUpload);
      showToast.success(res.message || 'File uploaded successfully!');
      fetchSummary();
      fetchEntries();
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message || err?.message || 'Error occurred during file upload.';
      showToast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadError(null);
    setUploadResult(null);
  };

  // Status History
  const openStatusHistory = async (adviceUniqueId) => {
    setStatusModalAdvice(adviceUniqueId);
    setLoadingHistory(true);
    try {
      const data = await careparvaReportService.getStatusHistory(adviceUniqueId);
      setStatusHistory(data || []);
    } catch (err) {
      console.error('Failed to load status history:', err);
      showToast.error('Could not load status history.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const closeStatusHistory = () => {
    setStatusModalAdvice(null);
    setStatusHistory([]);
  };

  // Logs Modal
  const openLogsModal = async () => {
    setShowLogsModal(true);
    setLoadingLogs(true);
    try {
      const data = await careparvaReportService.getLogs({ page: 1, limit: 30 });
      setLogsList(data.items || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
      showToast.error('Could not load upload logs.');
    } finally {
      setLoadingLogs(false);
    }
  };

  const closeLogsModal = () => {
    setShowLogsModal(false);
    setLogsList([]);
  };

  // Subtle Badge Renderers
  const renderPtypeBadge = (ptype) => {
    switch (ptype) {
      case 1:
        return <span className="badge-clean badge-clean-intraday">Intraday</span>;
      case 2:
        return <span className="badge-clean badge-clean-single">Single Stock</span>;
      case 3:
        return <span className="badge-clean badge-clean-derivative">Derivative</span>;
      default:
        return <span className="badge-clean badge-clean-closed">Unknown</span>;
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) return <span>-</span>;
    const clean = String(status).toUpperCase();
    if (clean === 'ACTIVE') return <span className="badge-clean badge-clean-active">ACTIVE</span>;
    if (clean === 'CLOSED') return <span className="badge-clean badge-clean-closed">CLOSED</span>;
    if (clean === 'INVALID') return <span className="badge-clean badge-clean-invalid">INVALID</span>;
    if (clean === 'REJECTED') return <span className="badge-clean badge-clean-rejected">REJECTED</span>;
    return <span className="badge-clean badge-clean-closed">{clean}</span>;
  };

  const renderSystemStatus = (status) => {
    if (status === null || status === undefined || status === '') return <span>-</span>;
    const num = Number(status);
    switch (num) {
      case 1:
        return <span className="badge-clean badge-clean-active">Active</span>;
      case 2:
        return <span className="badge-clean badge-clean-single">Target 1</span>;
      case 3:
        return <span className="badge-clean badge-clean-single">Target 2</span>;
      case 4:
        return <span className="badge-clean badge-clean-rejected">Stoploss</span>;
      case 5:
        return <span className="badge-clean badge-clean-closed">Closed</span>;
      default:
        return <span className="badge-clean badge-clean-closed">{status}</span>;
    }
  };

  // Pagination calculation
  const start = totalEntries > 0 ? (page - 1) * limit + 1 : 0;
  const end = Math.min(page * limit, totalEntries);

  return (
    <div className="careparva-container mt-5 pt-2">
      {/* Breadcrumb & Title Row */}
        <div className="row pb-2 align-items-center">
          <div className="col-md-6 col-sm-6">
            <ul className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="ti ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item">Research</li>
              <li className="breadcrumb-item active">Careparva Report</li>
            </ul>
            <h1 className="page-header m-0" style={{ fontSize: '20px', fontWeight: 600, color: '#242a30' }}>
              Careparva Report <small style={{ fontSize: '12px', color: '#6c757d' }}>Advisory Reconciliation &amp; Careparva Status Tracking</small>
            </h1>
          </div>

          <div className="col-md-6 col-sm-6 text-end d-flex justify-content-end align-items-center gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowUploadModal(true)}
              disabled={uploading}
            >
              <FontAwesomeIcon icon={uploading ? faSpinner : faUpload} spin={uploading} className="me-1" />
              {uploading ? 'Processing File...' : 'Upload Excel / CSV'}
            </button>
            <button className="btn btn-default btn-sm" onClick={openLogsModal}>
              <FontAwesomeIcon icon={faHistory} className="me-1" /> Upload Logs
            </button>
            <button className="btn btn-default btn-sm" onClick={handleRefresh} title="Refresh">
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || uploading} />
            </button>
          </div>
        </div>

        {/* Normal Light Count Panels */}
        <div className="row mb-3 g-3">
          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6">
            <div className="careparva-stat-panel">
              <div>
                <div className="stat-title">Total Entries</div>
                <div className="stat-value">{summary.total?.toLocaleString() || 0}</div>
              </div>
              <div className="stat-icon icon-blue">
                <FontAwesomeIcon icon={faList} />
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6">
            <div className="careparva-stat-panel">
              <div>
                <div className="stat-title">Intraday Details</div>
                <div className="stat-value">{summary.intradayCount?.toLocaleString() || 0}</div>
              </div>
              <div className="stat-icon icon-orange">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6">
            <div className="careparva-stat-panel">
              <div>
                <div className="stat-title">Single Stock Details</div>
                <div className="stat-value">{summary.singleStockCount?.toLocaleString() || 0}</div>
              </div>
              <div className="stat-icon icon-green">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6">
            <div className="careparva-stat-panel">
              <div>
                <div className="stat-title">Derivative Details</div>
                <div className="stat-value">{summary.derivativeCount?.toLocaleString() || 0}</div>
              </div>
              <div className="stat-icon icon-purple">
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
            </div>
          </div>
        </div>

        {/* Clean Filter Panel */}
        <div className="panel panel-default with-rounded-corner with-shadow mb-3 p-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            {/* Sheet Tabs */}
            <div>
              <ul className="careparva-nav-pills">
                {TABS.map((t) => (
                  <li key={t.id}>
                    <button
                      className={`nav-link ${activeTab === t.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(t.id)}
                    >
                      <FontAwesomeIcon icon={t.icon} className="me-1" />
                      {t.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Total Count Badge */}
            <div>
              <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '12px' }}>
                Total: {filteredEntries.length}
                {filteredEntries.length !== entries.length && ` (of ${entries.length})`}
              </span>
            </div>
          </div>
        </div>

        {/* Table in Subscriber Theme Panel Container */}
        <div className="panel panel-default with-rounded-corner with-shadow mb-3">
          <div
            className="table-responsive"
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 260px)',
              overflowX: 'auto',
              position: 'relative',
            }}
          >
            <table className="table table-condensed table-hover bg-white sticky-table careparva-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                  <th style={{ minWidth: '140px' }}>Service Name</th>
                  <th style={{ minWidth: '120px' }}>Symbol</th>
                  <th style={{ minWidth: '100px' }}>System Action</th>
                  <th style={{ minWidth: '110px' }}>Careparva Action</th>
                  <th style={{ minWidth: '110px' }}>System Status</th>
                  <th style={{ minWidth: '120px' }}>Careparva Status</th>
                  <th style={{ width: '180px', maxWidth: '200px' }}>Reason</th>
                  <th style={{ minWidth: '140px' }}>Created On</th>
                  <th style={{ textAlign: 'center', width: '70px', minWidth: '70px' }}>Action</th>
                </tr>
                <tr className="filter-row">
                  <th style={{ textAlign: 'center' }}></th>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={colFilters.service_name}
                      onChange={(e) => handleColFilterChange('service_name', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={colFilters.symbol_name}
                      onChange={(e) => handleColFilterChange('symbol_name', e.target.value)}
                    />
                  </th>
                  <th>
                    <select
                      value={colFilters.action}
                      onChange={(e) => handleColFilterChange('action', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="1">BUY</option>
                      <option value="0">SELL</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={colFilters.p_action}
                      onChange={(e) => handleColFilterChange('p_action', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="BUY">BUY</option>
                      <option value="SELL">SELL</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={colFilters.status}
                      onChange={(e) => handleColFilterChange('status', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="1">Active</option>
                      <option value="2">Target 1</option>
                      <option value="3">Target 2</option>
                      <option value="4">Stoploss</option>
                      <option value="5">Closed</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={colFilters.p_status}
                      onChange={(e) => handleColFilterChange('p_status', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="CLOSED">CLOSED</option>
                      <option value="INVALID">INVALID</option>
                      <option value="REJECTED">REJECTED</option>
                    </select>
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={colFilters.p_reason}
                      onChange={(e) => handleColFilterChange('p_reason', e.target.value)}
                    />
                  </th>
                  <th style={{ minWidth: '150px' }}>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={startDate}
                        onChange={(dates) => setCreatedDateRange(dates)}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        isClearable
                        placeholderText="Select Date"
                        dateFormat="dd-MM-yyyy"
                        monthsShown={2}
                        popperPlacement="bottom-end"
                      />
                    </div>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear Filters"
                      onClick={handleResetFilters}
                      style={{ padding: '2px 8px', fontSize: '10px', whiteSpace: 'nowrap' }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Loading Careparva records...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faFileExcel} className="me-2 text-secondary" />
                      {entries.length === 0
                        ? 'No Careparva records found. Click Upload Excel / CSV to import data.'
                        : 'No records matched the filter criteria.'}
                      {entries.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-link btn-xs ms-2 text-primary p-0"
                          onClick={handleResetFilters}
                        >
                          Reset filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((item, idx) => (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'center', color: '#868e96' }}>
                        {start + idx}
                      </td>
                      <td>
                        <strong className="text-dark">{item.service_name || '-'}</strong>
                      </td>
                      <td>
                        <span className="fw-semibold text-dark">{item.symbol_name || '-'}</span>
                      </td>
                      <td>
                        {item.action === 1 ? (
                          <span className="badge-clean badge-clean-buy">BUY</span>
                        ) : item.action === 0 ? (
                          <span className="badge-clean badge-clean-sell">SELL</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {item.p_action ? (
                          <span
                            className={`badge-clean ${
                              String(item.p_action).toUpperCase().includes('BUY')
                                ? 'badge-clean-buy'
                                : String(item.p_action).toUpperCase().includes('SELL')
                                ? 'badge-clean-sell'
                                : 'badge-clean-closed'
                            }`}
                          >
                            {item.p_action}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{renderSystemStatus(item.status)}</td>
                      <td>{renderStatusBadge(item.p_status)}</td>
                      <td style={{ maxWidth: '180px' }}>
                        <span
                          className="reason-cell"
                          title={item.p_reason || '-'}
                        >
                          {item.p_reason || '-'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                          {item.p_created_on ? (
                            <span>{item.p_created_on.substring(0, 19)}</span>
                          ) : item.n_created_on ? (
                            <span>{new Date(item.n_created_on).toLocaleDateString()}</span>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-default btn-xs text-primary"
                          style={{ padding: '2px 7px', fontSize: '12px' }}
                          title="View Full Details"
                          onClick={() => navigate(`/research/careparva-report/view/${item.id}`, { state: { entry: item } })}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upload File Modal */}
        {showUploadModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header d-flex justify-content-between align-items-center bg-white border-bottom">
                  <h5 className="modal-title font-weight-bold m-0 text-dark">
                    <FontAwesomeIcon icon={faUpload} className="me-2 text-primary" />
                    Upload Careparva Advice File
                  </h5>
                  <button
                    type="button"
                    className="close text-muted"
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '22px',
                      cursor: 'pointer',
                    }}
                    onClick={closeUploadModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body p-4 bg-white">
                  <div
                    className={`dropzone-upload-box ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx, .xls, .csv"
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                    />
                    <FontAwesomeIcon icon={faUpload} className="text-primary mb-2" style={{ fontSize: '36px' }} />
                    <h6 className="font-weight-bold mb-1">Click to browse or drag &amp; drop file</h6>
                    <p className="text-muted small mb-2">Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      <span className="badge bg-light text-dark border">Intraday Details</span>
                      <span className="badge bg-light text-dark border">Single Stock Details</span>
                      <span className="badge bg-light text-dark border">Derivative Details</span>
                    </div>
                  </div>

                  {selectedFile && (
                    <div className="alert alert-light border mt-3 mb-0 d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon
                          icon={selectedFile.name.endsWith('.csv') ? faFileCsv : faFileExcel}
                          className="text-primary fs-5"
                        />
                        <div>
                          <strong>{selectedFile.name}</strong>
                          <div className="small text-muted">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setSelectedFile(null)}
                        disabled={uploading}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  {uploadError && (
                    <div className="alert alert-danger mt-3 mb-0 d-flex align-items-start gap-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1" />
                      <div>
                        <strong>Upload Rejected:</strong>
                        <div>{uploadError}</div>
                      </div>
                    </div>
                  )}

                  {uploadResult && (
                    <div className="alert alert-success mt-3 mb-0">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <strong>{uploadResult.message}</strong>
                      </div>
                      <div className="d-flex gap-3">
                        <div className="bg-white p-2 rounded border flex-fill text-center">
                          <small className="text-muted d-block">Processed</small>
                          <strong className="fs-6">{uploadResult.totalProcessed}</strong>
                        </div>
                        <div className="bg-white p-2 rounded border flex-fill text-center">
                          <small className="text-success d-block">Inserted</small>
                          <strong className="fs-6 text-success">{uploadResult.insertCount}</strong>
                        </div>
                        <div className="bg-white p-2 rounded border flex-fill text-center">
                          <small className="text-primary d-block">Updated</small>
                          <strong className="fs-6 text-primary">{uploadResult.updateCount}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer bg-light border-top">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={closeUploadModal}
                    disabled={uploading}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleUploadSubmit}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Processing File...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} className="me-1" /> Start Processing
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status History Modal */}
        {statusModalAdvice && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header d-flex justify-content-between align-items-center bg-white border-bottom">
                  <h5 className="modal-title font-weight-bold m-0 text-dark">
                    Status History - <span className="text-primary">{statusModalAdvice}</span>
                  </h5>
                  <button
                    type="button"
                    className="close text-muted"
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '22px',
                      cursor: 'pointer',
                    }}
                    onClick={closeStatusHistory}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body p-0">
                  {loadingHistory ? (
                    <div className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Loading history...
                    </div>
                  ) : statusHistory.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No status history recorded for this advice.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Reason</th>
                            <th>Recorded Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statusHistory.map((h, i) => (
                            <tr key={i}>
                              <td>{renderStatusBadge(h.status)}</td>
                              <td>{h.reason || '-'}</td>
                              <td className="text-muted small">
                                {h.created_on ? new Date(h.created_on).toLocaleString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="modal-footer bg-light border-top">
                  <button type="button" className="btn btn-default btn-sm" onClick={closeStatusHistory}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Logs Modal */}
        {showLogsModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header d-flex justify-content-between align-items-center bg-white border-bottom">
                  <h5 className="modal-title font-weight-bold m-0 text-dark">
                    <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" />
                    Uploaded Files &amp; Audit Logs
                  </h5>
                  <button
                    type="button"
                    className="close text-muted"
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '22px',
                      cursor: 'pointer',
                    }}
                    onClick={closeLogsModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body p-0">
                  {loadingLogs ? (
                    <div className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Loading logs...
                    </div>
                  ) : logsList.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No upload logs recorded yet.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Uploaded File Name</th>
                            <th style={{ textAlign: 'center' }}>Inserted</th>
                            <th style={{ textAlign: 'center' }}>Updated</th>
                            <th>Upload Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logsList.map((log) => (
                            <tr key={log.id}>
                              <td>
                                <strong className="text-primary">{log.sheet_name}</strong>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="badge-clean badge-clean-buy">{log.insert_count}</span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="badge-clean badge-clean-derivative">{log.update_count}</span>
                              </td>
                              <td className="text-muted small">
                                {log.created_on ? new Date(log.created_on).toLocaleString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default CareparvaReport;
